import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    driveways_size: '',
    service_type: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch('https://formspree.io/f/mkopwrbr', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', address: '', driveways_size: '', service_type: '', timeline: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  const inputClass = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm transition-all duration-200";

  return (
    <section id="contact" className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-16">
          <div className="text-orange-500 font-display font-bold text-lg uppercase tracking-widest mb-3">Contact Us</div>
          <h2 className="font-display text-6xl md:text-7xl font-bold text-white uppercase mb-6">Get Your Free Estimate</h2>
          <p className="text-xl text-gray-400 max-w-2xl">
            Professional sealcoating with transparent pricing. No hidden fees, no surprises.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-white/3 border border-white/10 rounded-2xl p-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-white text-sm font-semibold mb-2">Your Name *</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required className={inputClass} placeholder="Full name" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-white text-sm font-semibold mb-2">Phone Number *</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} required className={inputClass} placeholder="(262) 555-0123" />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-white text-sm font-semibold mb-2">Email Address</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} className={inputClass} placeholder="your@email.com" />
                </div>

                <div>
                  <label htmlFor="address" className="block text-white text-sm font-semibold mb-2">Property Address *</label>
                  <input type="text" id="address" name="address" value={formData.address} onChange={handleInputChange} required className={inputClass} placeholder="Street address, City, WI" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="driveways_size" className="block text-white text-sm font-semibold mb-2">Driveway Size</label>
                    <div className="relative">
                      <select id="driveways_size" name="driveways_size" value={formData.driveways_size} onChange={handleInputChange} className={`${inputClass} appearance-none pr-8`}>
                        <option value="">Select size</option>
                        <option value="small">Small (under 500 sq ft)</option>
                        <option value="medium">Medium (500–1,000 sq ft)</option>
                        <option value="large">Large (1,000–2,000 sq ft)</option>
                        <option value="extra-large">Extra Large (2,000+ sq ft)</option>
                      </select>
                      <i className="ri-arrow-down-s-line text-gray-400 absolute right-3 top-3.5 pointer-events-none"></i>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service_type" className="block text-white text-sm font-semibold mb-2">Service Needed *</label>
                    <div className="relative">
                      <select id="service_type" name="service_type" value={formData.service_type} onChange={handleInputChange} required className={`${inputClass} appearance-none pr-8`}>
                        <option value="">Select service</option>
                        <option value="sealcoating-only">Sealcoating Only</option>
                        <option value="complete-package">Complete Package (Crack Fill + Sealcoating)</option>
                        <option value="maintenance-package">Full Maintenance Package (Seal + Stripe)</option>
                        <option value="crack-fill-only">Crack Filling Only</option>
                        <option value="commercial-lot">Commercial Lot Sealcoating</option>
                        <option value="parking-striping">Parking Lot Striping</option>
                      </select>
                      <i className="ri-arrow-down-s-line text-gray-400 absolute right-3 top-3.5 pointer-events-none"></i>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="timeline" className="block text-white text-sm font-semibold mb-2">Preferred Timeline</label>
                  <div className="relative">
                    <select id="timeline" name="timeline" value={formData.timeline} onChange={handleInputChange} className={`${inputClass} appearance-none pr-8`}>
                      <option value="">Select timeline</option>
                      <option value="asap">ASAP (within 1 week)</option>
                      <option value="2-weeks">Within 2 weeks</option>
                      <option value="month">Within a month</option>
                      <option value="planning">Just planning ahead</option>
                    </select>
                    <i className="ri-arrow-down-s-line text-gray-400 absolute right-3 top-3.5 pointer-events-none"></i>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-white text-sm font-semibold mb-2">Additional Details</label>
                  <textarea
                    id="message" name="message" value={formData.message} onChange={handleInputChange}
                    rows={4} maxLength={500}
                    className={`${inputClass} resize-none`}
                    placeholder="Any specific concerns, driveway condition, or special requests..."
                  />
                  <div className="text-gray-500 text-xs mt-1 text-right">{formData.message.length}/500</div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-800 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-200 shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                  ) : (
                    <><i className="ri-send-plane-line"></i> Get My Free Estimate</>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                    <p className="text-green-300 text-sm flex items-center gap-2"><i className="ri-check-circle-line"></i>Your estimate request was sent! We'll respond within 2 hours.</p>
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                    <p className="text-red-300 text-sm flex items-center gap-2"><i className="ri-error-warning-line"></i>Something went wrong. Please call us at (262) 215-0688.</p>
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-orange-500 rounded-xl p-7 text-white">
              <h3 className="font-display text-2xl font-bold uppercase mb-1 flex items-center gap-2">
                <i className="ri-phone-fill"></i> Need Help Now?
              </h3>
              <p className="text-orange-100 text-sm mb-4">Call or text for instant response</p>
              <a href="tel:262-215-0688" className="font-display text-3xl font-bold block mb-4 hover:text-orange-200 transition-colors">(262) 215-0688</a>
              <div className="text-sm text-orange-100 space-y-2">
                <div className="flex items-center gap-2"><i className="ri-time-line"></i> Available 7 days a week</div>
                <div className="flex items-center gap-2"><i className="ri-chat-3-line"></i> Text messages welcome</div>
                <div className="flex items-center gap-2"><i className="ri-mail-line"></i> info@lakesareaseal.com</div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-7">
              <h3 className="font-display text-xl font-bold text-white uppercase mb-5">Why Choose Us?</h3>
              <div className="space-y-3">
                {[
                  { icon: 'ri-shield-check-line', text: 'Licensed & Insured' },
                  { icon: 'ri-money-dollar-circle-line', text: 'Free On-Site Estimates' },
                  { icon: 'ri-timer-line', text: '24-Hour Response Time' },
                  { icon: 'ri-thumb-up-line', text: '99% Customer Satisfaction' },
                  { icon: 'ri-map-pin-line', text: 'Local Walworth County Experts' }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 text-gray-300">
                    <div className="w-8 h-8 bg-orange-500/20 border border-orange-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${benefit.icon} text-orange-400 text-sm`}></i>
                    </div>
                    <span className="text-sm">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-7">
              <h3 className="font-display text-xl font-bold text-white uppercase mb-5">Service Areas</h3>
              <div className="text-gray-400 text-sm space-y-2">
                {['Elkhorn, WI', 'Lake Geneva', 'Williams Bay', 'Fontana-on-Geneva Lake', 'Delavan', 'Sharon', 'Walworth', 'East Troy'].map((area) => (
                  <div key={area} className="flex items-center gap-2">
                    <i className="ri-map-pin-2-line text-orange-500 text-xs"></i>
                    {area}
                  </div>
                ))}
                <div className="pt-2 text-orange-400 font-semibold flex items-center gap-2">
                  <i className="ri-add-line"></i> All of Walworth County
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
